import { type LeadDetail, type MissingField } from '@utazzo/shared';
import { UzDialog } from '@utazzo/ui';
import React from 'react';
import { toast } from 'sonner';

import { missingForActiveLead, missingTravelIntentForBacklog } from '../active-readiness';
import { LeadFormPage } from '../pages/LeadFormPage';

/**
 * Which readiness rule to evaluate on every render + on save:
 *   - `active`               : base 9-field set (default, unchanged behaviour).
 *   - `active+travelIntent`  : base 9-field set PLUS the six travel-intent
 *                              fields. Use when the server flag is on and the
 *                              operator is transitioning a lead to Active.
 *   - `travelIntent`         : ONLY the six travel-intent fields. Use for the
 *                              backlog gate on follow-up / publish - the lead
 *                              is already Active, so re-surfacing the base
 *                              set would confuse the seller.
 */
export type LeadMissingMode = 'active' | 'active+travelIntent' | 'travelIntent';

function computeMissing(lead: LeadDetail, mode: LeadMissingMode): MissingField[] {
  if (mode === 'travelIntent') return missingTravelIntentForBacklog(lead);
  return missingForActiveLead(lead, {
    includeTravelIntent: mode === 'active+travelIntent',
  });
}

/**
 * "Complete the lead before moving to Active" modal.
 *
 * Extracted 2026-07-09 so both the header status picker (LeadStatusChanger)
 * and the follow-up flow (LeadDetailPage's "Move to Active" call sub-status)
 * open the SAME modal with identical behaviour:
 *
 *   - Renders the embedded lead Edit form so operators fix the required
 *     fields (Travel start / end, Budget, etc.) without leaving the page.
 *   - Amber "Missing: ..." pill recomputes on every render off the live
 *     `lead` prop so it shrinks as the operator saves fields.
 *   - On save, re-checks readiness against the FRESH lead returned by the
 *     PATCH. If gaps remain, keeps the modal open + surfaces a toast so
 *     the operator sees exactly what's still missing (matches ADR-0054 §7).
 *   - Only fires `onReady(updatedLead)` when every required field is set;
 *     hosts use that callback to fire whatever action they were about to
 *     take (a stage transition, a follow-up-driven Move to Active, etc.).
 */
export const LeadMissingFieldsModal = ({
  open,
  onOpenChange,
  lead,
  onReady,
  targetStatusId,
  targetSubStatusId,
  title = 'Complete the lead before moving to Active',
  description = 'The following fields are required to mark this lead as Active.',
  mode = 'active',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: LeadDetail;
  /**
   * Called when the operator's save has filled every required active
   * field. Host is responsible for firing the pending action (transition
   * / mutation) and closing the modal.
   */
  onReady: (updatedLead: LeadDetail) => void;
  /**
   * 2026-07-09. When set, preseeds the embedded form's inner Query
   * status / Sub status pickers to the outer header picker's target
   * (e.g. "Active" / "Proposal"). A Save Changes click will then fire
   * the transition in the same round-trip, so the header pill reflects
   * the new stage after one save. Falls back to the lead's current
   * status when omitted.
   */
  targetStatusId?: string | null;
  targetSubStatusId?: string | null;
  title?: string;
  description?: string;
  /** See LeadMissingMode. Defaults to 'active' for the legacy pre-transition
   *  behaviour so existing callers stay unaffected. */
  mode?: LeadMissingMode;
}): React.JSX.Element => {
  return (
    <UzDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="2xl"
    >
      <div className="space-y-4">
        {(() => {
          const missing = computeMissing(lead, mode);
          if (missing.length === 0) return null;
          return (
            // 2026-07-09. Sticky at the top of the scroll container so
            // the operator always sees exactly which fields are still
            // missing while scrolling through the long embedded lead
            // Edit form below. `top-0` is relative to the dialog's
            // scroll body (UzDialog wraps children in an overflow-y
            // container). 2026-07-16: the old `-mx-6 px-6` full-bleed
            // made the pill 48px wider than the scroll body, and since
            // `overflow-y: auto` forces `overflow-x` to auto, that
            // produced a permanent horizontal scrollbar. The pill now
            // spans exactly the scroll body's width - content is
            // clipped at the same edge, so nothing scrolls past it.
            <div className="sticky top-0 z-10 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 shadow-sm">
              <span className="font-medium">Missing: </span>
              {missing.map((m) => m.label).join(', ')}
            </div>
          );
        })()}
        <LeadFormPage
          leadIdOverride={lead.id}
          embedded
          // 2026-07-09. Only the sections that participate in the
          // active-readiness gate render inside this modal (Enquiry +
          // Customer + Travel-core). The "Additional travel details"
          // block (TTV / meal plan / expected payment date / etc.) and
          // the "Notes" card stay hidden so the operator focuses on
          // exactly what the modal is asking them to fill.
          focusOnActiveReadiness
          // Reveal the "Additional travel details" section when the mode
          // requires travel-intent fields; those inputs live in that block.
          focusIncludeTravelIntent={mode !== 'active'}
          initialStatusIdOverride={targetStatusId ?? undefined}
          initialSubStatusIdOverride={targetSubStatusId ?? undefined}
          onSaveSuccess={(_leadId, updatedLead) => {
            if (!updatedLead) return;
            const stillMissing = computeMissing(updatedLead, mode);
            if (stillMissing.length > 0) {
              toast.error(`Still missing: ${stillMissing.map((m) => m.label).join(', ')}`);
              return;
            }
            onReady(updatedLead);
          }}
        />
      </div>
    </UzDialog>
  );
};
